import {
  useEffect,
  useRef,
  useState,
  type MouseEvent as ReactMouseEvent,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from 'react';
import { IconButton } from '../primitives/IconButton.tsx';
import styles from './Navigation.module.css';

/**
 * Design system § 14 — Navigation. Two patterns and nothing else.
 *
 * There is deliberately no sidebar component, at any viewport. § 2 lists the
 * grey sidebar first among the anti-patterns, and § 14 says "never a sidebar"
 * in as many words, so the absence here is the design rather than an omission.
 */

export interface NavigationItem {
  id: string;
  label: string;
  current?: boolean;
}

/**
 * Turns the whole top-level row into an accordion that expands SIDEWAYS to
 * reveal a second row of items in the same pill, rather than swapping the
 * pill's contents outright — the admin nav's areas are a step the trigger
 * makes room for, not a navigation to somewhere else (design system § 2, § 14:
 * never a sidebar, never a second nav, and this stays the one pill).
 *
 * Opening it collapses every top-level item away — the trigger (`triggerId`)
 * included — so the open pill shows only the back control and `items`, not
 * "Aprenda" / "Ouvido" / "Administração" sitting alongside them.
 */
interface ExpandableGroup {
  triggerId: string;
  open: boolean;
  items: NavigationItem[];
  backLabel: string;
  onBack: () => void;
}

interface SegmentedNavProps {
  items: NavigationItem[];
  label: string;
  /** Floating docks the pill to the bottom on mobile, per § 11. */
  placement?: 'inline' | 'floating';
  onSelect?: (id: string) => void;
  expand?: ExpandableGroup;
}

export function SegmentedNav({
  items,
  label,
  placement = 'inline',
  onSelect,
  expand,
}: SegmentedNavProps) {
  const scrollerRef = useRef<HTMLDivElement>(null);
  /*
   * Drag state lives in refs, not state — a pointer move firing at up to
   * 120Hz has no business going through React's render cycle. `moved` is
   * what turns a drag into a scroll rather than a mis-click: below the
   * threshold, the pointer-up is left alone and the button underneath still
   * fires its own `click` as if nothing happened.
   */
  const drag = useRef<{
    pointerId: number;
    startX: number;
    startScrollLeft: number;
    moved: boolean;
  } | null>(null);
  const suppressClick = useRef(false);

  /*
   * The grab affordance only appears once there is somewhere to grab TO —
   * the common short pill (the main nav's four tabs) never earns it. Content
   * can change scrollability without the container's own box changing size
   * (a longer label, an added tab, the admin group opening), so this
   * re-checks on every render of the items/expand state it's handed, not
   * only on a `ResizeObserver` callback.
   */
  const [scrollable, setScrollable] = useState(false);
  useEffect(() => {
    const check = () => {
      const scroller = scrollerRef.current;
      setScrollable(scroller ? scroller.scrollWidth > scroller.clientWidth + 1 : false);
    };
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, [items, expand?.open, expand?.items]);

  function handlePointerDown(event: ReactPointerEvent<HTMLDivElement>) {
    // Touch and pen already scroll this natively; only a mouse needs a
    // click-and-drag substitute for the horizontal wheel/trackpad gesture.
    if (event.pointerType !== 'mouse' || event.button !== 0) return;
    const scroller = scrollerRef.current;
    if (!scroller || scroller.scrollWidth <= scroller.clientWidth) return;
    drag.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startScrollLeft: scroller.scrollLeft,
      moved: false,
    };
    // Capture is deferred to the first real movement in handlePointerMove,
    // not taken here. Capturing on every mousedown — including a plain click
    // that never moves — retargets the compatibility mouseup/click events to
    // this container instead of the button underneath, so the button never
    // sees its own click and navigation silently stops working.
  }

  function handlePointerMove(event: ReactPointerEvent<HTMLDivElement>) {
    const state = drag.current;
    const scroller = scrollerRef.current;
    if (!state || !scroller) return;
    const delta = event.clientX - state.startX;
    if (!state.moved && Math.abs(delta) > 3) {
      state.moved = true;
      scroller.setPointerCapture(state.pointerId);
    }
    if (state.moved) scroller.scrollLeft = state.startScrollLeft - delta;
  }

  function endDrag(event: ReactPointerEvent<HTMLDivElement>) {
    const state = drag.current;
    const scroller = scrollerRef.current;
    if (state) suppressClick.current = state.moved;
    if (scroller?.hasPointerCapture(event.pointerId)) scroller.releasePointerCapture(event.pointerId);
    drag.current = null;
  }

  // A drag that ended over a tab still bubbles a `click` — this is what
  // keeps that from also selecting the tab the pointer happened to land on.
  function handleClickCapture(event: ReactMouseEvent<HTMLDivElement>) {
    if (suppressClick.current) {
      suppressClick.current = false;
      event.preventDefault();
      event.stopPropagation();
    }
  }

  function renderSegment(item: NavigationItem) {
    return (
      <button
        key={item.id}
        type="button"
        className={`${styles.segment} type-button`}
        aria-current={item.current ? 'page' : undefined}
        onClick={() => onSelect?.(item.id)}
      >
        {item.label}
      </button>
    );
  }

  return (
    <nav aria-label={label} className={styles.wrapper}>
      <div
        ref={scrollerRef}
        className={styles.segmented}
        data-placement={placement}
        data-scrollable={scrollable || undefined}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        onClickCapture={handleClickCapture}
      >
        {expand ? (
          <span className={styles.expandGroup} data-open={expand.open || undefined}>
            <span className={styles.collapseSlot} data-open={expand.open || undefined}>
              <span className={styles.collapseInner} inert={expand.open}>
                {items.map((item) => renderSegment(item))}
              </span>
            </span>

            <span className={styles.expandSlot} data-side="leading">
              <span className={styles.expandInner} inert={!expand.open}>
                <IconButton
                  icon="arrow-left"
                  label={expand.backLabel}
                  shape="square"
                  size="compact"
                  onClick={expand.onBack}
                />
              </span>
            </span>

            <span className={styles.expandSlot} data-side="trailing">
              <span className={styles.expandInner} inert={!expand.open}>
                {expand.items.map((sub) => renderSegment(sub))}
              </span>
            </span>
          </span>
        ) : (
          items.map((item) => renderSegment(item))
        )}
      </div>
    </nav>
  );
}

interface TaskBarProps {
  /** The accessible name of the escape — "Leave lesson", say. */
  backLabel: string;
  onBack: () => void;
  /** The progress element. A ProgressBar, normally. */
  progress?: ReactNode;
}

export function TaskBar({ backLabel, onBack, progress }: TaskBarProps) {
  return (
    <nav className={styles.taskBar} aria-label={backLabel}>
      <IconButton icon="arrow-left" label={backLabel} shape="square" onClick={onBack} />
      <span className={styles.taskBarProgress}>{progress}</span>
    </nav>
  );
}
